import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { hasSessionEvents } from "@/shared/domain/orgs";
import { useOrgEventModel } from "@/shared/domain/orgs/hooks";
import { type PaginatedResponse } from "@/shared/http";

import { eventQueryKeys } from "./eventQueryKeys";

/** Page size requested per search. Bounded so the caller renders a short list
 *  and the user types to narrow rather than scrolling. */
const SEARCH_LIMIT = 20;

/**
 * Lazy, server-side search over an org's event tags.
 *
 * Returns a stable function that resolves one page of matching tags. Unlike
 * {@link useEventTags}, nothing is fetched until it is called.
 */
export function useEventTagSearch(
  orgId: string,
): (
  search: string,
  signal?: AbortSignal,
) => Promise<PaginatedResponse<string>> {
  const { events } = useDomainServices();
  const queryClient = useQueryClient();
  const includeSessionEvents = hasSessionEvents(useOrgEventModel(orgId));

  return React.useCallback(
    (search, signal) => {
      const fetchPage = (abortSignal?: AbortSignal) =>
        events.getTagsForOrg(
          { search: search || undefined, limit: SEARCH_LIMIT },
          { resourceOwnerId: orgId, abortSignal, includeSessionEvents },
        );

      if (search !== "") {
        return fetchPage(signal);
      }

      return queryClient.ensureQueryData({
        queryKey: [
          ...eventQueryKeys.orgTagSearch(orgId, ""),
          includeSessionEvents,
        ],
        queryFn: ({ signal: querySignal }) => fetchPage(querySignal),
      });
    },
    [queryClient, events, orgId, includeSessionEvents],
  );
}
